import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError, type JobNote } from '../lib/api.js';
import { useAuth } from '../lib/auth-store.js';
import { Button } from './ui/button.js';
import { Feedback } from './ui/feedback.js';
import { Pill, type PillVariant } from './ui/pill.js';
import { QueryError } from './ui/query-error.js';

export function jobNotesQueryKey(spireId: number) {
  return ['sales-order', spireId, 'notes'] as const;
}

/**
 * Written notes on a job — the sales rep's quote notes, the office's notes
 * for the tech, and techs' field notes. They're stored as Spire CRM notes;
 * this is how they become visible inside Tinsmith.
 *
 * `canAdd` turns on the office's add-note box (order page). Techs add theirs
 * from the on-site Notes tile, which has the offline outbox behind it.
 */
export function JobNotes({
  spireId,
  emptyHint = 'No notes on this job.',
  canAdd = false,
}: {
  spireId: number;
  emptyHint?: string;
  canAdd?: boolean;
}) {
  // Office roles only: a field tech's note belongs on the on-site Notes tile
  // (tagged Field, offline-safe) — here it would be mislabelled "Office".
  const role = useAuth((s) => s.user?.role);
  const showAdd = canAdd && role !== undefined && role !== 'field_tech';
  const query = useQuery({
    queryKey: jobNotesQueryKey(spireId),
    queryFn: ({ signal }) => api.salesOrders.listNotes(spireId, signal),
    staleTime: 60_000,
  });

  return (
    <div className="space-y-3">
      {showAdd && <AddOfficeNote spireId={spireId} />}
      {query.isPending ? (
        <div className="text-sm text-ink-muted">Loading notes…</div>
      ) : query.isError ? (
        <QueryError
          title="Couldn't load notes"
          error={query.error}
          onRetry={() => void query.refetch()}
          size="md"
        />
      ) : query.data.length === 0 ? (
        <div className="text-sm text-ink-muted">{emptyHint}</div>
      ) : (
        <ul className="space-y-2">
          {query.data.map((n) => (
            <NoteRow key={n.noteId} note={n} />
          ))}
        </ul>
      )}
    </div>
  );
}

/** CRM-note subject the API classifies as an office note (NOTE_SUBJECTS.office). */
const OFFICE_NOTE_SUBJECT = 'Tinsmith office note';

function AddOfficeNote({ spireId }: { spireId: number }) {
  const queryClient = useQueryClient();
  const [text, setText] = useState('');
  const add = useMutation<unknown, ApiError, string>({
    mutationFn: (body) => api.salesOrders.addNote(spireId, { body, subject: OFFICE_NOTE_SUBJECT }),
    onSuccess: async () => {
      setText('');
      await queryClient.invalidateQueries({ queryKey: jobNotesQueryKey(spireId) });
    },
  });
  const trimmed = text.trim();

  return (
    <form
      className="space-y-2"
      onSubmit={(e) => {
        e.preventDefault();
        if (trimmed && !add.isPending) add.mutate(trimmed);
      }}
    >
      <label className="sr-only" htmlFor={`office-note-${spireId}`}>
        Note for the tech
      </label>
      <textarea
        id={`office-note-${spireId}`}
        value={text}
        onChange={(e) => setText(e.target.value)}
        rows={3}
        maxLength={4000}
        placeholder="Note for the tech — gate code, who to ask for, what to bring…"
        className="block w-full rounded-lg border bg-surface px-3 py-2 text-base text-ink placeholder:text-ink-subtle focus:outline-none focus:ring-2 focus:ring-brand-300 focus:border-brand"
        style={{ borderColor: 'var(--color-border-secondary)' }}
      />
      <div className="flex items-center gap-3">
        <Button type="submit" size="sm" disabled={!trimmed || add.isPending}>
          {add.isPending ? 'Saving…' : 'Add note'}
        </Button>
        <span className="text-xs text-ink-subtle">Shows on the tech's job and in Spire's CRM notes.</span>
      </div>
      {add.isError && (
        <Feedback variant="error" size="sm">
          Couldn't save the note: {add.error.message}
        </Feedback>
      )}
    </form>
  );
}

const KIND_PILL: Record<JobNote['kind'], { variant: PillVariant; label: string }> = {
  quote: { variant: 'sales', label: 'Sales' },
  office: { variant: 'install', label: 'Office' },
  field: { variant: 'info', label: 'Field' },
};

function NoteRow({ note }: { note: JobNote }) {
  const when = note.createdAt
    ? new Date(note.createdAt).toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' })
    : null;
  return (
    <li
      className="rounded-lg border p-2.5"
      style={{ borderColor: 'var(--color-border-tertiary)' }}
    >
      <div className="flex items-center gap-2 text-xs text-ink-muted">
        <Pill variant={KIND_PILL[note.kind].variant}>{KIND_PILL[note.kind].label}</Pill>
        <span className="truncate">{[when, note.createdBy].filter(Boolean).join(' · ')}</span>
      </div>
      <p className="mt-1 whitespace-pre-wrap break-words text-sm text-ink">{note.body}</p>
    </li>
  );
}
